import type { Address } from "viem"
import { useCallback, useEffect, useState } from "react"
import { addMinutes, getUnixTime } from "date-fns"
import { observer } from "mobx-react-lite"
import { useNavigate, useParams } from "react-router-dom"
import { useLocalStorage } from "usehooks-ts"
import { useAccount, useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ILiquidityPoolTypeCard, LiquidityPoolTypeCard } from "@/domains/common/components/liquidity-pool-type-card"
import { SlippageInnerCard200 } from "@/domains/common/components/slippage-inner-card-200"
import { DEFAULT_SLIPPAGE, MAX_SLIPPAGE, MIN_SLIPPAGE } from "@/domains/common/config/slippage"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { createGaugeInteraction } from "@/domains/interactions"
import { addLiquidityInteraction } from "@/domains/interactions/add-liquidity/add-liquidity-interaction"
import { clampNumber, cn } from "@/domains/library"
import { LiquidityPageLayout } from "@/domains/liquidity-page/components/liquidity-page-layout"
import { LiquidityPageTabs } from "@/domains/liquidity-page/components/liquidity-page-tabs"
import { SlippageSettingsModal } from "@/domains/minimal-pool/components/minimal-pool-dialog/slippage-settings-modal"
import { ClassicLiquidityPagesComplexBreadcrumbApplication } from "@/domains/navigation/components/complex-breadcrumb-applications/classic-liquidity-pages-complex-breadcrumb-application"
import { ClassicLiquiditySubItem } from "@/domains/navigation/subitems/classic-liquidity-subitem"
import { ILiquidityPool } from "@/domains/pools"
import { TokenInputCard } from "@/domains/tokens/components/token-input-card"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useMinimalPool } from "@/hooks/use-minimal-pool"
import useEventEmitter from "@/hooks/useEventEmitter"

const deadlineMinutes = 20

const BREADCRUMB_ITEMS = [new ClassicLiquiditySubItem(), ClassicLiquidityPagesComplexBreadcrumbApplication]

const AddPairLiquidityPage = observer(() => {
  const navigate = useNavigate()
  const { address: poolAddressParam } = useParams<{ address: string }>()
  const account = useAccount()
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const governanceToken = useGovernanceToken()
  const [isSlippageDialogOpen, setIsSlippageDialogOpen] = useState(false)
  const scaleApp = useScaleApplication()

  const poolAddress = poolAddressParam ? (poolAddressParam as Address) : undefined
  const minimalPool = useMinimalPool(poolAddress)

  // slippage
  // defaultSlippage may have come from local storage and is not safe,
  // lets ensure it's a valid number between MIN_SLIPPAGE and MAX_SLIPPAGE
  const [defaultSlippage, setDefaultSlippage] = useLocalStorage("swap-default-slippage", DEFAULT_SLIPPAGE)
  if (defaultSlippage <= MIN_SLIPPAGE || defaultSlippage >= MAX_SLIPPAGE) {
    setDefaultSlippage(DEFAULT_SLIPPAGE)
  }
  const [slippage, setSlippage] = useState(Number(defaultSlippage).toString())

  const handleSetSlippage = useCallback(
    (newSlippage: string) => {
      setSlippage(newSlippage)
      setDefaultSlippage(Number(newSlippage))
    },
    [setSlippage, setDefaultSlippage],
  )

  // Make pool state
  const [amountToken0, setAmountToken0] = useState("0")
  const [amountToken1, setAmountToken1] = useState("0")
  const [errorToken0, setErrorToken0] = useState<boolean>(false)
  const [errorToken1, setErrorToken1] = useState<boolean>(false)

  // Hook up the orchestrator to run the loadUserManageMinimalPoolInfo query
  useEffect(() => {
    if (!minimalPool || !scaleApp) return
    scaleApp.MinimalPools.Orchestrator.startUserManagePoolSync(minimalPool, 30_000)
    return () => {
      scaleApp.MinimalPools.Orchestrator.stopUserManagePoolSync()
    }
  }, [minimalPool, scaleApp])

  const pairExists = Boolean(minimalPool)

  const canMakeLiquidity =
    Number(amountToken0) > 0 && Number(amountToken1) > 0 && !errorToken0 && !errorToken1 && pairExists

  const clearMakeValues = () => {
    setAmountToken0("0")
    setAmountToken1("0")
    setErrorToken0(false)
    setErrorToken1(false)
  }

  const setMakePairAmountsFromToken0 = (newAmount: string) => {
    if (!newAmount) {
      clearMakeValues()
      return
    }
    if (!minimalPool) return

    const reserve0 = minimalPool.reserve0
    const reserve1 = minimalPool.reserve1

    const amount0Wei = minimalPool.token0.parseUnits(newAmount)
    const amount1Wei = (amount0Wei * reserve1) / reserve0

    setAmountToken0(newAmount)
    setAmountToken1(minimalPool.token1.toFixed(amount1Wei))
    setErrorToken0(amount0Wei > minimalPool.token0.balance)
    setErrorToken1(amount1Wei > minimalPool.token1.balance)
  }

  const setMakePairAmountsFromToken1 = (newAmount: string) => {
    if (!newAmount) {
      clearMakeValues()
      return
    }
    if (!minimalPool) return

    const reserve0 = minimalPool.reserve0
    const reserve1 = minimalPool.reserve1

    const amount1Wei = minimalPool.token1.parseUnits(newAmount)
    const amount0Wei = (amount1Wei * reserve0) / reserve1

    setAmountToken0(minimalPool.token0.toFixed(amount0Wei))
    setAmountToken1(newAmount)
    setErrorToken0(amount0Wei > minimalPool.token0.balance)
    setErrorToken1(amount1Wei > minimalPool.token1.balance)
  }

  const addAction = async () => {
    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }

    if (!minimalPool || !chainConfig) {
      console.error("Missing required dependencies")
      return
    }

    const chainId = chainConfig.chainId
    const deadline = getUnixTime(addMinutes(new Date(), clampNumber(deadlineMinutes, 1, 60)))

    const amount0 = minimalPool.token0.parseUnits(amountToken0)
    const amount1 = minimalPool.token1.parseUnits(amountToken1)

    const _result = await addLiquidityInteraction({
      amount0,
      amount1,
      approvalMode: scaleApp?.Settings.approvalMode,
      chainId,
      config,
      connectedAddress: scaleApp.Wallet.Connection.connectedAddress.toViemAddress(),
      deadline,
      emitter,
      minimalPool,
      routerAddress: chainConfig.equalizer.contracts.router,
      slippage: Number(slippage),
      onSuccess: () => {
        void clearMakeValues()
        scaleApp.MinimalPools.Orchestrator.userInfoPairsQueryInstance?.forceExecute()
        scaleApp.Tokens.Orchestrator.balanceQuery?.forceExecute()
      },
    })
  }

  //#region do we need to show the create gauge button?
  const hasGauge = Boolean(minimalPool?.hasGauge ?? false)

  let canCreateGauge = !hasGauge

  // if (!hasGauge) {
  //   const isToken0Whitelisted = Boolean(token0?.isWhitelisted ?? false)
  //   const isToken0Equal = isAddressEqual(token0.address, governanceToken.address)
  //   const isToken0WrappedToken = isAddressEqual(token0.address, wrappedToken.address)
  //   const isToken1Whitelisted = Boolean(token1?.isWhitelisted ?? false)
  //   const isToken1Equal = isAddressEqual(token1.address, governanceToken.address)
  //   const isToken1WrappedToken = isAddressEqual(token1.address, wrappedToken.address)

  //   canCreateGauge = canCreateGaugeLogic({
  //     isToken0Whitelisted,
  //     isToken0Equal,
  //     isToken0WrappedToken,
  //     isToken1Whitelisted,
  //     isToken1Equal,
  //     isToken1WrappedToken,
  //   })
  // }
  //#endregion

  //#region create gauge interaction
  const onCreateGaugeClick = async () => {
    if (!account.address) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }

    if (
      !minimalPool ||
      !chainConfig ||
      !emitter ||
      !chainConfig.chainId ||
      !config ||
      !minimalPool.address ||
      !account.address ||
      !minimalPool.displayName ||
      !chainConfig.equalizer.contracts.voter
    ) {
      console.error("Missing required dependencies")
      return
    }
    const result = await createGaugeInteraction({
      account: account.address, // Address type is already inferred from account.address
      chainId: chainConfig.chainId,
      config,
      pairAddress: minimalPool.address.toViemAddress(), // Address type is already inferred from pair.address
      pairDisplayName: minimalPool.displayName,
      voterAddress: chainConfig.equalizer.contracts.voter, // Address type is already inferred
      emitter,
    })

    if (result.success && chainConfig.chainId) {
      const pairAddress = minimalPool.address.toViemAddress()
      scaleApp.PendingCreation.setPendingGauge({
        gaugeAddress: pairAddress, // Placeholder; we poll by pairAddress via findByRevenueSource
        pairAddress,
        pairDisplayName: minimalPool.displayName ?? "",
        chainId: chainConfig.chainId,
      })
      navigate(`/liquidity/${minimalPool.address.toString()}/gauge-creating`, { replace: true })
    }
  }

  //#endregion

  const title = minimalPool ? `Add ${minimalPool.displayName} Liquidity` : "Add Liquidity"

  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle
          title="Add Liquidity"
          items={BREADCRUMB_ITEMS}
          subTitle={`to ${minimalPool?.displayName} and earn ${governanceToken.symbol}`}
        />

        <LiquidityPageLayout minimalPool={minimalPool} page="add">
          <LiquidityPageTabs />
          <Card.Container className="flex w-full min-w-[320px] flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
            <div className="flex items-center justify-between gap-3">
              <LiquidityPoolTypeCard pool={minimalPool as unknown as ILiquidityPoolTypeCard} />
              <div className="flex flex-row items-center gap-1 text-xs text-t200">
                <SlippageSettingsModal
                  isOpen={isSlippageDialogOpen}
                  slippage={slippage}
                  title="Add Liquidity Settings"
                  onSlippageChanged={(v) => handleSetSlippage(v)}
                  onClose={(isOpen) => {
                    const slippageNum = Number(slippage)
                    if (slippageNum === 0) {
                      handleSetSlippage(DEFAULT_SLIPPAGE.toString())
                    } else if (slippageNum > MAX_SLIPPAGE) {
                      handleSetSlippage(DEFAULT_SLIPPAGE.toString())
                    } else if (slippageNum < MIN_SLIPPAGE) {
                      handleSetSlippage(MIN_SLIPPAGE.toString())
                    }
                    setIsSlippageDialogOpen(isOpen)
                  }}
                />
                <SlippageInnerCard200
                  onChangeSlippage={() => setIsSlippageDialogOpen(!isSlippageDialogOpen)}
                  slippage={Number(slippage)}
                />
              </div>
            </div>

            {minimalPool?.token0 !== undefined ? (
              <TokenInputCard
                amount={amountToken0}
                showTokenSelectable={false}
                token={minimalPool?.token0}
                tokens={[]}
                onTokenAmountChange={setMakePairAmountsFromToken0}
              />
            ) : null}
            {minimalPool?.token1 !== undefined ? (
              <TokenInputCard
                amount={amountToken1}
                showTokenSelectable={false}
                token={minimalPool?.token1}
                tokens={[]}
                onTokenAmountChange={setMakePairAmountsFromToken1}
              />
            ) : null}
            <div className={cn("flex w-full", canCreateGauge ? "justify-between" : "justify-end")}>
              <ShowWhenTrue when={canCreateGauge}>
                <Button variant={"neutral"} onClick={onCreateGaugeClick} size="fit">
                  Create Gauge
                </Button>
              </ShowWhenTrue>
              <Button disabled={!canMakeLiquidity} variant={"primary"} onClick={addAction} size="fit">
                Add Liquidity
              </Button>
            </div>
          </Card.Container>
        </LiquidityPageLayout>
      </PageContainer>
    </TooltipProvider>
  )
})

export default AddPairLiquidityPage
